import React, {
  FC,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  ReactElement,
  CSSProperties,
} from 'react'
import { Carousel, Skeleton } from 'antd'
import { CarouselRef } from 'antd/lib/carousel'
import Modal from '../../common/modal/modal'
import Avatar from '../../common/avatar/avatar'

import styles from './popup-gallery.module.scss'
import './popup-gallery.ant.scss'
import ImageGallery, { PropsImageGallery } from './image/image-gallery'
import VideoGallery from './video/video-gallery'

import { SliderArrowRight, SliderArrowLeft, Close, Download } from '../../common/icons'

export type MediaAuthor = {
  authorName?: string
  avatar?: string | null
}

interface PopupGalleryProps {
  label?: string
  author?: MediaAuthor | null
  captions?: string[]
  visible: boolean
  startSlide?: number
  totalSlides?: number
  onCancel?: () => void
  onLoad?: (flagOnLoad?: string) => void | undefined
  inAlbum?: boolean
}

interface IPopupGallery extends FC<PopupGalleryProps> {
  Image: typeof ImageGallery
  Video: typeof VideoGallery
}

const PopupGallery: IPopupGallery = (props) => {
  const { label, author, captions, visible, onCancel, startSlide = 0, onLoad, totalSlides, children, inAlbum } = props
  const boxForSliderRef = useRef<HTMLElement | null>()
  const carouselRef = useRef<CarouselRef | null>(null)
  const pointerPosition = useRef<{ clientX: number; clientY: number } | null>(null)

  const [currentSlide, setCurrentSlide] = useState(startSlide)
  const [hoveredArrow, setHoveredArrow] = useState<'left' | 'right' | null>(null)
  const activeChild = React.Children.toArray(children)[currentSlide]
  const downloadUrl = React.isValidElement<{ src?: string }>(activeChild) ? activeChild.props.src : undefined
  const isPhoto = React.isValidElement(activeChild) && activeChild.type === ImageGallery
  const [imageSize, setImageSize] = useState({ slide: -1, width: 0 })
  const handleImageWidthChange = useCallback(
    (width: number) => setImageSize({ slide: currentSlide, width }),
    [currentSlide],
  )
  const [downloadRequest, setDownloadRequest] = useState(false)
  const [countSlide, setCountSlide] = useState(React.Children.count(children))

  useLayoutEffect(() => {
    // Reset the counter and active media for every viewing session, including the same thumbnail.
    setCurrentSlide(startSlide)
    setHoveredArrow(null)
    pointerPosition.current = null
  }, [startSlide, visible])

  useEffect(() => {
    const countChildren = React.Children.count(children)
    if (countSlide !== countChildren) {
      setDownloadRequest(false)
      setCountSlide(countChildren)
    }
  }, [children, setDownloadRequest, countSlide])

  useEffect(() => {
    if (!visible || countSlide < 2) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
      if (
        event.target instanceof Element &&
        event.target.closest('input, textarea, select, [contenteditable="true"], [role="slider"], video')
      ) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      if (event.key === 'ArrowLeft') carouselRef.current?.prev()
      else carouselRef.current?.next()
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [visible, countSlide])

  useEffect(() => {
    if (
      onLoad &&
      totalSlides &&
      countSlide &&
      countSlide < totalSlides &&
      currentSlide + 4 >= countSlide &&
      !downloadRequest
    ) {
      setDownloadRequest(true)
      onLoad()
    }
  }, [setDownloadRequest, onLoad, totalSlides, countSlide, currentSlide, downloadRequest])

  const handleChangeSlide = (from: number, to: number) => {
    setCurrentSlide(to)
    const nextChild = React.Children.toArray(children)[to]
    if (!React.isValidElement(nextChild) || nextChild.type !== ImageGallery) setHoveredArrow(null)
  }

  const getPhotoSide = useCallback(
    (position: { clientX: number; clientY: number }): 'left' | 'right' | null => {
      const image = boxForSliderRef.current?.querySelector(
        `.slick-slide[data-index="${currentSlide}"] .popup-gallery-image img`,
      )
      if (!image) return null
      const bounds = image.getBoundingClientRect()
      const { clientX, clientY } = position
      const inside =
        clientX >= bounds.left && clientX <= bounds.right && clientY >= bounds.top && clientY <= bounds.bottom
      return inside ? (clientX < bounds.left + bounds.width / 2 ? 'left' : 'right') : null
    },
    [currentSlide],
  )

  useLayoutEffect(() => {
    if (imageSize.slide === currentSlide && imageSize.width > 0 && pointerPosition.current) {
      setHoveredArrow(getPhotoSide(pointerPosition.current))
    }
  }, [imageSize, currentSlide, getPhotoSide])

  const handlePhotoClick = (event: React.MouseEvent<HTMLElement>) => {
    if ((event.target as Element).closest('.slick-arrow, .video-gallery')) return
    if (!isPhoto) return
    const image = event.currentTarget.querySelector('.slick-active .popup-gallery-image img')
    if (event.target !== image) {
      onCancel?.()
      return
    }
    if (countSlide <= 1) return
    pointerPosition.current = { clientX: event.clientX, clientY: event.clientY }
    const side = getPhotoSide(event)
    if (side === 'left') carouselRef.current?.prev()
    if (side === 'right') carouselRef.current?.next()
  }

  // solving the problem with timeline and volume sound in firefox
  useEffect(() => {
    let slickListHTMLElement: HTMLElement | null
    const callback = (e: MouseEvent) => {
      e.stopPropagation()
    }

    if (boxForSliderRef.current && visible) {
      slickListHTMLElement = boxForSliderRef.current.querySelector('.slick-list')
      slickListHTMLElement?.addEventListener('mouseup', callback)
    }

    return () => {
      slickListHTMLElement?.removeEventListener('mouseup', callback)
    }
  }, [visible, totalSlides, countSlide, currentSlide, downloadRequest])

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      destroyOnClose={true}
      footer={null}
      className={'popup-gallery'}
      wrapClassName={styles.popupGalleryMask}
      maskClosable={true}
      maskStyle={{ backgroundColor: 'rgba(48, 48, 48, 0.96)' }}
      closable={false}
      wrapProps={{ onContextMenu: (event: React.MouseEvent) => event.stopPropagation() }}
    >
      <header className={styles.header}>
        <div className={`${styles.boxSpan} ${styles.headerTitle} ${inAlbum ? '' : styles.headerTitleFullwidth}`}>
          {author !== undefined ? (
            <div className={styles.author}>
              <Avatar image={author?.avatar} size="medium" />
              <span className={styles.album}>{author?.authorName || 'Пользователь удалён'}</span>
            </div>
          ) : (
            <span className={`${styles.album} ${inAlbum ? '' : styles.albumFullwidth}`}>{label}</span>
          )}
        </div>
        {inAlbum && (
          <div className={styles.boxSpan}>
            <span className={styles.navigation}>
              {currentSlide + 1} из {totalSlides}
            </span>
          </div>
        )}
        {downloadUrl && (
          <a
            className={styles.download}
            href={downloadUrl}
            download={captions?.[currentSlide] || true}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Скачать файл"
            title="Скачать файл"
          >
            <Download />
          </a>
        )}
        <button type="button" className="ant-modal-close" aria-label="Close" onClick={onCancel}>
          <span className="ant-modal-close-x">
            <Close color="#ffffff" />
          </span>
        </button>
      </header>
      <section
        ref={(section) => (boxForSliderRef.current = section)}
        data-photo={isPhoto ? 'true' : undefined}
        data-arrow-side={hoveredArrow || undefined}
        onMouseMove={(event) => {
          pointerPosition.current = { clientX: event.clientX, clientY: event.clientY }
          setHoveredArrow(getPhotoSide(event))
        }}
        onMouseLeave={() => {
          pointerPosition.current = null
          setHoveredArrow(null)
        }}
        onClick={handlePhotoClick}
        style={
          {
            '--gallery-arrow-inset':
              isPhoto && imageSize.width > 0 ? `max(8px, calc((100% - ${imageSize.width}px) / 2 + 8px))` : '8px',
          } as CSSProperties
        }
      >
        <Carousel
          ref={carouselRef}
          key={String(visible)}
          initialSlide={countSlide ? startSlide : 0}
          dots={false}
          arrows={true}
          fade={true}
          waitForAnimate={false}
          swipe={true}
          beforeChange={handleChangeSlide}
          nextArrow={
            <SlickButton>
              <SliderArrowRight color="#ffffff" />
            </SlickButton>
          }
          prevArrow={
            <SlickButton>
              <SliderArrowLeft color="#ffffff" />
            </SlickButton>
          }
        >
          {countSlide ? (
            React.Children.map(children, (child, index) => {
              if (child) {
                return React.cloneElement(child as ReactElement<PropsImageGallery>, {
                  visible: currentSlide === index,
                  ...(React.isValidElement(child) && child.type === ImageGallery
                    ? { onWidthChange: handleImageWidthChange }
                    : {}),
                })
              }
            })
          ) : (
            <div className={styles.boxSkeletonImage}>
              <Skeleton.Image />
            </div>
          )}
        </Carousel>
      </section>
      {captions?.[currentSlide] && <div className={styles.caption}>{captions[currentSlide]}</div>}
    </Modal>
  )
}

type PropsSlickButton = {
  children: JSX.Element
  currentSlide?: number
  slideCount?: number
}

const SlickButton: FC<PropsSlickButton> = ({ currentSlide, slideCount, children, ...props }) => {
  // in Ant, HTML attribute is filled incorrectly due to which an error occurs
  // error: React does not recognize the `slideCount` prop on a DOM element
  // so we create a wrapper and pass only the required properties
  // the conflict field is not passed and therefore does not appear in the DOM
  return <div {...props}>{children}</div>
}

PopupGallery.defaultProps = {
  startSlide: 0,
  totalSlides: 0,
  visible: false,
  label: 'Альбом',
}

PopupGallery.Image = ImageGallery
PopupGallery.Video = VideoGallery
export default PopupGallery
